'use client';

import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { cva } from 'class-variance-authority';
import { useGetTooltipContent } from '../../../hooks/useGetTooltipContent';
import { classes } from '../../../utils/classes';
import { SoftHyphenInjector } from '../../SoftHyphenInjector';
import type { HeadlineType } from '../types';

const headlineStyles = cva('hyphens-auto after:mr-0 after:ml-0', {
  variants: {
    textAlign: {
      center: 'text-center',
      left: 'text-left',
      right: 'text-right',
    },
    addSpacer: {
      false: null,
      true: [
        'after:border-b-solid',
        'after:border-color-primary',
        'after:my-1',
        'after:block',
        'after:w-[3.875rem]',
        'after:border-b-[0.125rem]',
        "after:content-['']",
      ],
    },
  },
  compoundVariants: [
    {
      addSpacer: true,
      textAlign: ['center', 'right'],
      class: 'after:ml-auto',
    },
    {
      addSpacer: true,
      textAlign: ['center', 'left'],
      class: 'after:mr-auto',
    },
  ],
});

export const MainHeadline = ({ data }: { data: HeadlineType }) => {
  const {
    field_variation: styleVariation,
    field_level_style: styleLevel,
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
    field_level: mainHeadlineLevel = 1,
    field_no_h_element: noHeadlineTag,
    field_text: headlineText,
    field_text_align: textAlign,
    field_font: headlineFont,
    field_font_color: fontColor,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
  } = data;

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  // when styleVariation is set to 2, add a spacer
  const addSpacer = styleVariation === 2;

  // What the main headline looks like
  const mainHeadlineStyleLevel = styleLevel || mainHeadlineLevel;

  const MainHeadlineElement = noHeadlineTag ? 'div' : (`h${mainHeadlineLevel}` as const);

  return (
    <MainHeadlineElement
      className={classes(
        `text-size-h${mainHeadlineStyleLevel}`,
        headlineFont,
        headlineStyles({ textAlign, addSpacer }),
      )}
      style={{ color: fontColor }}
    >
      {tooltipContent && !!tooltipActivation ? (
        <Tooltip variant="inline" content={tooltipContent} positioning={tooltipPositioning}>
          <SoftHyphenInjector text={headlineText} />
        </Tooltip>
      ) : (
        <SoftHyphenInjector text={headlineText} />
      )}
    </MainHeadlineElement>
  );
};
